/** Seven-language UI copy for explicit owner-only opponent merges. */
export const OPPONENT_MERGE_OVERLAY: Record<string, { opponentMerge: Record<string, string> }> = {
  en: { opponentMerge: {
    title: "Merge these opponent profiles?",
    subtitle: "Choose which name and profile to keep. Only your private matches will be updated.",
    keep: "Keep this profile",
    moveCount: "Matches to move",
    connectedCount: "Already linked matches",
    safety: "Match dates, scores, ratings, technical observations and notes remain unchanged. The old opponent label will be standardized.",
    confirmSamePerson: "Confirm that these names refer to the SAME person.",
    merge: "Merge profiles",
    merging: "Merging…",
    success: "Profiles combined. {{count}} private matches reassigned.",
    error: "Could not merge. Review any connected match, pending invitation or changed profile before retrying.",
    blocked: "This pair contains a linked account or Connected Match. It cannot be merged here.",
    cancel: "Cancel",
  } },
  el: { opponentMerge: {
    title: "Σύμπτυξη αυτών των αντιπάλων;",
    subtitle: "Επίλεξε ποιο όνομα και προφίλ θα διατηρηθεί. Μόνο οι προσωπικοί αγώνες σου μπορούν να ενημερωθούν.",
    keep: "Διατήρηση αυτού του προφίλ",
    moveCount: "Αγώνες που θα μεταφερθούν",
    connectedCount: "Συνδεδεμένοι αγώνες",
    safety: "Οι ημερομηνίες, τα σκορ, οι αξιολογήσεις, οι παρατηρήσεις και οι σημειώσεις κάθε αγώνα μένουν ανέπαφα. Ενοποιείται μόνο η αναγραφή του αντιπάλου.",
    confirmSamePerson: "Επιβεβαιώνω ότι πρόκειται για το ΙΔΙΟ πρόσωπο.",
    merge: "Σύμπτυξη προφίλ",
    merging: "Γίνεται σύμπτυξη…",
    success: "Τα προφίλ ενοποιήθηκαν. Μεταφέρθηκαν {{count}} προσωπικοί αγώνες.",
    error: "Η σύμπτυξη δεν έγινε. Έλεγξε για Connected Match, εκκρεμή πρόσκληση ή αλλαγή στοιχείων και ξαναπροσπάθησε.",
    blocked: "Ένα από τα προφίλ συμμετέχει σε συνδεδεμένο λογαριασμό ή αγώνα. Δεν επιτρέπεται σύμπτυξη από εδώ.",
    cancel: "Ακύρωση",
  } },
  es: { opponentMerge: {
    title: "¿Fusionar estos perfiles de rival?",
    subtitle: "Elige qué nombre y perfil conservar. Solo se actualizarán tus partidos privados.",
    keep: "Conservar este perfil",
    moveCount: "Partidos que se trasladarán",
    connectedCount: "Partidos conectados",
    safety: "Fechas, resultados, valoraciones y observaciones originales permanecen intactos. Solo se unifica el nombre del rival.",
    confirmSamePerson: "Confirmo que ambos nombres se refieren a la MISMA persona.",
    merge: "Fusionar perfiles",
    merging: "Fusionando…",
    success: "Perfiles fusionados. Se reasignaron {{count}} partidos privados.",
    error: "No se pudo fusionar. Comprueba los partidos conectados, invitaciones pendientes o cambios en los perfiles.",
    blocked: "Algún perfil pertenece a una cuenta o partido conectado. No se puede fusionar aquí.",
    cancel: "Cancelar",
  } },
  fr: { opponentMerge: {
    title: "Fusionner ces profils d’adversaire ?",
    subtitle: "Choisissez le nom et le profil à conserver. Seuls vos matchs privés seront modifiés.",
    keep: "Conserver ce profil",
    moveCount: "Matchs à réattribuer",
    connectedCount: "Matchs connectés",
    safety: "Dates, scores, évaluations et observations restent inchangés. Seul le nom de l’adversaire sera harmonisé.",
    confirmSamePerson: "Je confirme qu’il s’agit de la MÊME personne.",
    merge: "Fusionner les profils",
    merging: "Fusion en cours…",
    success: "Profils fusionnés. {{count}} matchs privés réattribués.",
    error: "Fusion impossible. Vérifiez les matchs connectés, les invitations en attente et les changements de profil.",
    blocked: "Un profil est lié à un compte ou à un match connecté. La fusion est impossible ici.",
    cancel: "Annuler",
  } },
  de: { opponentMerge: {
    title: "Diese Gegnerprofile zusammenführen?",
    subtitle: "Wähle, welchen Namen und welches Profil du behalten möchtest. Nur deine privaten Matches werden aktualisiert.",
    keep: "Dieses Profil behalten",
    moveCount: "Zu übertragende Matches",
    connectedCount: "Verknüpfte Matches",
    safety: "Daten, Ergebnisse, Bewertungen und Beobachtungen bleiben unverändert. Nur die Gegnerbezeichnung wird vereinheitlicht.",
    confirmSamePerson: "Ich bestätige, dass beide Namen zur GLEICHEN Person gehören.",
    merge: "Profile zusammenführen",
    merging: "Zusammenführung läuft…",
    success: "Profile zusammengeführt. {{count}} private Matches neu zugeordnet.",
    error: "Zusammenführung fehlgeschlagen. Prüfe verknüpfte Matches, ausstehende Einladungen oder Profiländerungen.",
    blocked: "Ein Profil gehört zu einem verbundenen Konto oder Match und kann hier nicht zusammengeführt werden.",
    cancel: "Abbrechen",
  } },
  it: { opponentMerge: {
    title: "Unire questi profili avversario?",
    subtitle: "Scegli quale nome e profilo conservare. Verranno aggiornate solo le tue partite private.",
    keep: "Mantieni questo profilo",
    moveCount: "Partite da riassegnare",
    connectedCount: "Partite collegate",
    safety: "Date, punteggi, valutazioni e osservazioni restano invariati. Verrà uniformato solo il nome dell’avversario.",
    confirmSamePerson: "Confermo che i due nomi indicano la STESSA persona.",
    merge: "Unisci i profili",
    merging: "Unione in corso…",
    success: "Profili uniti. {{count}} partite private riassegnate.",
    error: "Impossibile unire. Controlla partite collegate, inviti in sospeso o modifiche ai profili.",
    blocked: "Un profilo è legato a un account o a una partita collegata. Non puoi unirlo qui.",
    cancel: "Annulla",
  } },
  ja: { opponentMerge: {
    title: "対戦相手のプロフィールを統合しますか？",
    subtitle: "残す名前とプロフィールを選んでください。変更されるのは自分の非連携試合のみです。",
    keep: "このプロフィールを残す",
    moveCount: "移行する試合",
    connectedCount: "連携済みの試合",
    safety: "日付、スコア、評価、各試合の記録は変更されません。対戦相手の表示名のみ統一します。",
    confirmSamePerson: "両方の名前が同一人物を指すことを確認しました。",
    merge: "プロフィールを統合",
    merging: "統合中…",
    success: "プロフィールを統合し、{{count}} 試合を移行しました。",
    error: "統合できませんでした。連携済み試合、保留中の招待、プロフィールの変更を確認してください。",
    blocked: "いずれかのプロフィールが連携済みアカウントまたは試合に関連しているため、統合できません。",
    cancel: "キャンセル",
  } },
};
